import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, signInAnonymously } from 'firebase/auth';
import {
    getFirestore,
    collection,
    getDoc,
    doc,
    setDoc,
    onSnapshot,
    updateDoc,
    setLogLevel // Import for debugging
} from 'firebase/firestore';
import { getAnalytics } from "firebase/analytics"; // New import for Analytics
import {
    LucideHeart,
    LucideShare2,
    LucideMessageCircle,
    LucideChevronLeft,
    LucideChevronRight,
    LucideStar,
    LucideMaximize,
    LucideSquare,
    LucidePlay,
    LucideSparkles,
    LucideSave,
    LucideBookOpen
} from 'lucide-react';
import './index.css';
import IMAGE_URLS from './IMAGE_URLSX';

const CYCLE_DURATION_MS = 2000; // 2 seconds

// --- Global Configuration Variables and Constants ---
// Use an empty API key string. The environment provides the actual key at runtime.
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || process.env.REACT_APP_FIREBASE_API_KEY || "";

// Updated to the correct, stable model version for text generation
const TEXT_MODEL = "gemini-2.5-flash-preview-09-2025";

// Tailwind class definition for a standard secondary button (Sidebar)
const SECONDARY_BTN_CLASSES = "w-full p-2 px-4 rounded-lg font-semibold transition-colors duration-200 shadow-md bg-slate-600 hover:bg-slate-500 text-slate-200 text-sm flex items-center justify-center border border-white/100";

// Tailwind class definition for a standard primary button (Actions)
const PRIMARY_BTN_CLASSES = "p-2 px-4 rounded-lg font-semibold transition-colors duration-200 shadow-md text-sm flex items-center justify-center bg-indigo-500 hover:bg-indigo-400 text-white border border-white/100";

// Ad HTML Content
const LEADERBOARD_AD_HTML = `
    <div style="font-family: Inter, sans-serif;" class="w-full h-20 bg-gradient-to-r from-cyan-400 to-teal-500 rounded-lg flex items-center justify-between p-4 shadow-xl">
        <div class="flex items-center">
            <svg class="w-8 h-8 text-yellow-700 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h14"></path></svg>
            <div>
                <h2 class="text-gray-800 text-sm font-bold">Meta Oakley HSTN - Transition Lenses®</h2>
                <p class="text-green-900 text-sm">Meta Smart AI Glasses and Lenses</p>
            </div>
        </div>
        <a href="https://amzn.to/3WUteDm" target="_blank" rel="noopener noreferrer" class="px-4 py-2 bg-pink-600 text-white font-bold rounded-full hover:bg-pink-500 transition-colors shadow-md text-sm">
            Visit Meta Store
        </a>
    </div>
`;

// --- User-Provided Firebase Configuration ---
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: "jopper-arts.web.app",
    projectId: "jopper-arts",
    storageBucket: "jopper-arts.firebasestorage.app",
    messagingSenderId: "303290051799",
    appId: "1:303290051799:web:7d54b9f81963e23cca9492",
    measurementId: "G-3HC0R23HKM"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export { db };

export const useUsers = () => {
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            const querySnapshot = await getDoc(collection(db, "users"));
            const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUsers(data);
        };

        fetchUsers();
    }, []);

    return users;
};

// ✅ Use the correct Firebase config
//const firebaseConfig = {

// Your web app's Firebase configuration (read from env)
// For Create React App, env vars must be prefixed with REACT_APP_
//const firebaseConfig = {
//apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
//authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
//projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
//storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
//messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
//appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
//measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ""
//};

// --- Utility Functions ---
// Base64 encoding/decoding for safe Firestore keys
const encodeUrlAsKey = (url) => btoa(url).replace(/\//g, '_').replace(/\+/g, '-').replace(/=/g, '');
const decodeUrlFromKey = (key) => atob(key.replace(/_/g, '/').replace(/-/g, '+'));

// Exponential Backoff for API calls
const withExponentialBackoff = async (apiCall, maxRetries = 5) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            const response = await apiCall();
            if (response.ok) return response;

            // Handle HTTP errors, e.g., rate limiting (429)
            if (response.status === 429 && attempt < maxRetries - 1) {
                const delay = Math.pow(2, attempt) * 1000 + Math.random() * 1000;
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            }
            throw new Error(`API call failed with status ${response.status}`);
        } catch (error) {
            if (attempt === maxRetries - 1) throw error;

            // Exponential delay
            const delay = Math.pow(2, attempt) * 1000 + Math.random() * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
};

/**
 * Generates an optimized Unsplash image URL using query parameters 
 * to ensure maximum sharpness for the current view state.
 * @param {string} baseUrl - The base image URL (without query params).
 * @param {boolean} isFullScreen - True if the image is in expanded view.
 * @returns {string} The optimized image URL.
 */

// ✅ Enhanced Version for better image resolution
const getOptimizedImageUrl = (baseUrl, isFullScreen = false) => {
    const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const scale = Math.ceil(devicePixelRatio);
    //const scale = devicePixelRatio > 1.5 ? 2 : 1;

    // Base parameters for sharpness and format
    const baseParams = [
        "auto=format",           // Let Unsplash choose best format (WebP, AVIF, etc.)
        "fit=max",              // Crop to exact dimensions
        "sharp=10",              // Max sharpness (0–10)
        `dpr=${devicePixelRatio}` // Device pixel ratio for retina displays
        //"dpr=" + devicePixelRatio // Device pixel ratio for retina displays
    ];

    let width, height, quality;

    if (isFullScreen) {
        width = 1200 * scale;
        height = 1600 * scale;
        quality = 95;
    } else {
        width = 800 * scale;
        height = 1067 * scale;
        quality = 85;
    }

    baseParams.push(`w=${width}`, `h=${height}`, `q=${quality}`);

    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${baseParams.join("&")}`;
};

// --- Main React Component ---
const App = ({ user, artworks }) => {
    // Canvas Global variables
    const [appId, setAppId] = useState('jopper-arts'); // Default app ID

    // State variables
    const [db, setDb] = useState(null);
    const [, setAuth] = useState(null);
    const [userId, setUserId] = useState(null);
    const [artData, setArtData] = useState({});
    const [imageIndex, setImageIndex] = useState(0);
    const [message, setMessage] = useState(null);
    const [comment, setComment] = useState("");
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [showQR, setShowQR] = useState(false);
    const [showFavorites, setShowFavorites] = useState(false);
    const [isAutoPlaying, setIsAutoPlaying] = useState(false);
    const [isAuthReady, setIsAuthReady] = useState(false);

    // Gemini Critique State
    const [critique, setCritique] = useState(null);
    const [isCritiquing, setIsCritiquing] = useState(false);

    // Gemini Context State
    const [contextData, setContextData] = useState(null); // { text: string, sources: [{uri, title}] }
    const [isContextLoading, setIsContextLoading] = useState(false);

    // Ad State
    const [adHtmlInput, setAdHtmlInput] = useState('<h1 style="color: blue; text-align: center;">Sample Ad Content</h1>');
    const [savedAdHtml, setSavedAdHtml] = useState(LEADERBOARD_AD_HTML);

    // NEW: State for the logo URL
    const [logoUrl, ] = useState('https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEh9K4fnFGJLlw8XdQ3f2GssEpezmkvx_gk9Yfm0KvmJFkLQ8oD5SJW2ivcW86eYkbtaqvZkZzkuLD_Z6BJkrK15dx9H5_jV8dLVcD8eiKoICOsspFfG8Lwz9wj-L9mQvgmE7e8fd0vWd9UJXzeMOkbRg5sRaQTChJ73f2CBSMwjcA-A-IrtdF6atsC_lD8/s863/logo_jopper_use.png');

    // NEW: State for determining if the current user is an editor (developer)
    const [isEditor, setIsEditor] = useState(false);

    const currentBaseUrl = IMAGE_URLS[imageIndex];
    // This dynamically generates the optimized URL based on isFullScreen state
    const currentImageUrl = getOptimizedImageUrl(currentBaseUrl, isFullScreen);

    const encodedUrlKey = encodeUrlAsKey(currentBaseUrl);
    const currentArt = artData[encodedUrlKey] || { likes: 0, comments: [], favorites: [] };
    const favoriteImages = Object.keys(artData).filter(urlKey => (artData[urlKey].favorites || []).includes(userId));

    const textPart = currentImageUrl.split('?text=')[1];

    // Derive a title from the URL or use a generic title if needed
    const currentArtTitle = textPart ? textPart.split('+').join(' ') : `Artwork ${imageIndex + 1}`;

    // --- Firebase Initialization and Data Fetching ---
    useEffect(() => {
        setLogLevel('debug');

        const currentAppId = typeof 'globalThis'.__app_id !== 'undefined'
            ? 'globalThis'.__app_id
            : (process.env.REACT_APP_APP_ID || 'jopper-arts');

        // support optional injected JSON string (legacy) but prefer env or top-level firebaseConfig
        let injectedConfig = null;
        if (typeof 'globalThis'.__firebase_config !== 'undefined') {
            try {
                injectedConfig = JSON.parse('globalThis'.__firebase_config);
            } catch (e) {
                console.warn("Failed to parse injected __firebase_config:", e);
            }
        }

        const initialAuthToken = typeof 'globalThis'.__initial_auth_token !== 'undefined'
            ? 'globalThis'.__initial_auth_token
            : (process.env.REACT_APP_INITIAL_AUTH_TOKEN || null);

        // NEW: Check for editor role
        const editorRole = typeof 'globalThis'.__app_user_role !== 'undefined'
            && ('globalThis'.__app_user_role === 'editor' || 'globalThis'.__app_user_role === 'owner');
        setIsEditor(editorRole);

        setAppId(currentAppId);

        const configToUse = injectedConfig || firebaseConfig;
        const app = initializeApp(configToUse);
        const firestore = getFirestore(app);
        const authInstance = getAuth(app);

        // Initialize Firebase Analytics
        getAnalytics(app);

        // Set Firestore debug level after initialization for robustness.
        setLogLevel('debug');

        setDb(firestore);
        setAuth(authInstance);

        // Authentication Handler: Sign in with token or anonymously
        const unsubscribeAuth = authInstance.onAuthStateChanged(async (user) => {
            if (user) {
                setUserId(user.uid);
                // Note: Last viewed index loading is handled by the separate useEffect below
            } else {
                try {
                    if (initialAuthToken) {
                        await signInWithCustomToken(authInstance, initialAuthToken);
                    } else {
                        await signInAnonymously(authInstance);
                    }
                    // The onAuthStateChanged listener will update the user state upon successful sign-in
                } catch (error) {
                    console.error("Firebase Auth Error:", error);
                }
            }
            setIsAuthReady(true);
        });

        // Cleanup function for onAuthStateChanged
        return () => unsubscribeAuth();
    }, []);

    // Firestore Real-time Data Listener
    useEffect(() => {
        if (!db || !userId || !appId) return;

        // Public data is used for collaborative interactions (likes, comments)
        const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'art_gallery_data', 'gallery-data');

        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setArtData(docSnap.data());
            } else {
                // Initialize the document if it doesn't exist
                setDoc(docRef, {});
            }
        }, (error) => {
            console.error("Firestore Snapshot Error:", error);
        });

        return () => unsubscribe();
    }, [db, userId, appId]);

    // --- Navigation and Interaction Handlers ---
    const navigateImage = useCallback(async (direction) => {
        // Clear dynamic data when navigating
        setCritique(null);
        setContextData(null);

        if (!showFavorites) {
            // Navigate through the static IMAGE_URLS
            setImageIndex(prevIndex => (prevIndex + direction + IMAGE_URLS.length) % IMAGE_URLS.length);
        } else if (favoriteImages.length > 0) {
            // Navigate through favorites
            const currentFavIndex = favoriteImages.findIndex(key => key === encodedUrlKey);

            // If the current image isn't in the list (e.g. was just unfavorited), default to the first
            const startIndex = currentFavIndex === -1 ? 0 : currentFavIndex;

            const nextFavIndex = (startIndex + direction + favoriteImages.length) % favoriteImages.length;
            const nextUrlKey = favoriteImages[nextFavIndex];

            // Find the index in the original IMAGE_URLS array
            setImageIndex(IMAGE_URLS.indexOf(decodeUrlFromKey(nextUrlKey)));
        }
    }, [showFavorites, favoriteImages, encodedUrlKey]);

    // Function to toggle the auto-play state
    const toggleAutoPlay = () => {
        setIsAutoPlaying(prev => !prev);
    };

    // --- useEffect for Auto-Play Interval ---
    useEffect(() => {
        let interval;

        if (isAutoPlaying) {
            // Start the interval when auto-play is enabled
            interval = setInterval(() => {
                navigateImage(1);
            }, CYCLE_DURATION_MS);
        }

        // Cleanup function: Clear the interval when the component unmounts 
        // OR when isAutoPlaying changes to false
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };

    }, [isAutoPlaying, navigateImage]); // Dependency on nextImage is safe due to useCallback

    // -----------------------------------------------------------
    // --- NEW FUNCTION: Save last viewed index to Firestore ---
    const saveLastViewed = useCallback(async (userId, index) => {
        if (!db) return; // Guard against null db
        try {
            await setDoc(doc(db, "users", userId), {
                lastViewedIndex: index,
            }, { merge: true });
        } catch (error) {
            console.error("Failed to save last viewed index:", error);
        }
    }, [db]);

    const loadLastViewed = useCallback(async (userId) => {
        if (!db) return 0; // Guard against null db
        try {
            const userDoc = await getDoc(doc(db, "users", userId));
            if (userDoc.exists()) {
                const data = userDoc.data();
                return data.lastViewedIndex || 0;
            }
        } catch (error) {
            console.error("Error loading last viewed index:", error);
        }
        return 0;
    }, [db]);

    // --- Load on mount/when user becomes available ---
    useEffect(() => {
        if (!db || !userId) return; // Guard against null db or userId

        const loadSavedIndex = async () => {
            try {
                const savedIndex = await loadLastViewed(userId);
                // Validate index is within bounds
                if (typeof savedIndex === 'number' && savedIndex >= 0 && savedIndex < IMAGE_URLS.length) {
                    setImageIndex(savedIndex);
                    console.log(`Resumed to image index ${savedIndex}`);
                } else {
                    console.log("Saved index out of bounds, using default");
                }
            } catch (error) {
                console.error("Error resuming session:", error);
            }
        };

        loadSavedIndex();
    }, [db, userId, loadLastViewed]); // Only reload when db or userId changes (not on loadLastViewed changes)

    // --- Save when index changes ---
    useEffect(() => {
        if (!db || !userId) return; // Guard against null db or userId
        saveLastViewed(userId, imageIndex);
        localStorage.setItem("lastViewedIndex", imageIndex);
    }, [imageIndex, db, userId, saveLastViewed]);

    // -----------------------------------------------------------

    // --- UI Helpers ---
    const showMessage = (title, msg, component = null) => {
        setMessage({ title, msg, component });
    };

    const toggleFullscreen = () => {
        setIsFullScreen(prev => !prev);
    };

    const saveAdCode = () => {
        setSavedAdHtml(adHtmlInput);
        showMessage("Success", "Ad code saved and ready to display!");
    };

    const shareOnTwitter = (url) => {
        const text = `Check out this amazing artwork: ${url}`;
        window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
    };

    const shareOnFacebook = (url) => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    };

    // --- Render Loading State ---
    if (!isAuthReady) {
        return (
            <div className="flex items-center justify-center h-screen text-white bg-gray-900">
                <div className="w-12 h-12 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
                <p className="ml-4">Authenticating User...</p>
            </div>
        );
    };

    const toggleFavoritesView = () => {
        const isCurrentlyViewingFavorites = showFavorites;

        if (isCurrentlyViewingFavorites) {
            // Transition back to main gallery, reset index
            setShowFavorites(false);
            setImageIndex(0);
        } else if (favoriteImages.length > 0) {
            // Transition to favorites, set to first favorite image
            setShowFavorites(true);
            setImageIndex(IMAGE_URLS.indexOf(decodeUrlFromKey(favoriteImages[0])));
        } else {
            showMessage("Favorites", "You have no favorite images yet. Start liking some artwork.!");
        }
    };

    const handleDataAction = async (field, value, actionType) => {
        if (!db || !userId) {
            showMessage("Error", "Authentication failed. Please ensure you are logged in.");
            return;
        }

        const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'art_gallery_data', 'gallery-data');
        const currentData = currentArt[field] || [];

        let newValue;
        let successMsg = "";
        let errorMsg = "";
        let updateObject = {};

        switch (actionType) {
            case 'like':
                // Likes are cumulative and don't need a deep array merge
                newValue = (currentArt.likes || 0) + 1;
                updateObject = { [`${encodedUrlKey}.likes`]: newValue };
                successMsg = "Liked!";
                break;
            case 'add_comment':
                if (!value || value.trim() === "") return showMessage("Error", "You cannot post empty comment.");
                newValue = [...currentData, `${userId.substring(0, 4)}...: ${value}`];
                updateObject = { [`${encodedUrlKey}.comments`]: newValue };
                successMsg = "Comment posted!";
                break;
            case 'toggle_favorite':
                if (currentData.includes(userId)) {
                    newValue = currentData.filter(id => id !== userId);
                    successMsg = "Removed from favorites!";
                } else {
                    newValue = [...currentData, userId];
                    successMsg = "Added to favorites!";
                }
                updateObject = { [`${encodedUrlKey}.favorites`]: newValue };
                break;
            default:
                return;
        }

        try {
            // Update the nested map field: e.g., art_gallery_data.gallery-data.Abstract+Art.likes
            await updateDoc(docRef, updateObject);
            showMessage(field.charAt(0).toUpperCase() + field.slice(1), successMsg);

            // Fix: Clear comment input only after successful database write
            if (actionType === 'add_comment') {
                setComment("");
            }
        } catch (error) {
            console.error(`Firestore Error (${field}):`, error);
            showMessage("Error", errorMsg || `Failed to update ${field}.`);
        }
    };

    // --- Gemini API Handlers ---

    // 1. LLM Critique (Simple Text Generation)
    const generateCritique = async () => {
        setIsCritiquing(true);
        setCritique(null);

        const systemPrompt = `You are a concise, world-class art critic. Provide a short, one-paragraph critical analysis focusing on the concept, composition, and implied meaning of the artwork titled "${currentArtTitle}".`;
        const userQuery = `Analyze the artwork displayed: ${currentArtTitle}`;

        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${TEXT_MODEL}:generateContent?key=${API_KEY}`;

        const payload = {
            contents: [{ parts: [{ text: userQuery }] }],
            systemInstruction: { parts: [{ text: systemPrompt }] },
        };

        try {
            const response = await withExponentialBackoff(() => fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            }));

            const result = await response.json();
            const text = result.candidates?.[0]?.content?.parts?.[0]?.text || "Critique failed to generate. Please try again.";
            setCritique(text);

        } catch (error) {
            console.error("Gemini Critique Error:", error);
            setCritique("Failed to retrieve critique due to a network or API error.");
        } finally {
            setIsCritiquing(false);
        }
    };

    // 2. LLM Historical Context (with Google Search Grounding)
    const generateHistoricalContext = async () => {
        setIsContextLoading(true);
        setContextData(null);

        const systemPrompt = `You are an art historian specializing in contextualizing generative artworks. 
        Given the title of an artwork, provide a brief (2-3 sentence) summary of 1 contemporary art movement, styles, or artists that share thematic or compositional similarities. 
        The response must be grounded in real-world AI-generated artwork such as the current one in view.`;
        const userQuery = `Find historical context for the art piece titled: ${currentArtTitle}`;

        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${TEXT_MODEL}:generateContent?key=${API_KEY}`;

        const payload = {
            contents: [{ parts: [{ text: userQuery }] }],
            systemInstruction: { parts: [{ text: systemPrompt }] },
            // Enable Google Search Grounding
            tools: [{ "google_search": {} }],
        };

        try {
            const response = await withExponentialBackoff(() => fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            }));

            const result = await response.json();
            const candidate = result.candidates?.[0];
            const text = candidate?.content?.parts?.[0]?.text || "Failed to find historical context. Please try again.";

            let sources = [];
            const groundingMetadata = candidate?.groundingMetadata;
            if (groundingMetadata && groundingMetadata.groundingAttributions) {
                sources = groundingMetadata.groundingAttributions
                    .map(attribution => ({
                        uri: attribution.web?.uri,
                        title: attribution.web?.title,
                    }))
                    .filter(source => source.uri && source.title)
                    // Limit to 3 sources for display clarity
                    .slice(0, 3);
            }

            setContextData({ text, sources });

        } catch (error) {
            console.error("Gemini Context Error:", error);
            setContextData({ text: "Failed to retrieve historical context due to a network or API error.", sources: [] });
        } finally {
            setIsContextLoading(false);
        }
    };

    // --- Layout and Component Rendering ---

    // Message/Notification Modal Component
    const MessageModal = ({ title, message, onClose }) => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
            <div className="max-w-sm p-6 text-center border border-indigo-500 rounded-lg shadow-xl bg-slate-700">
                <h3 className="text-xl font-bold text-yellow-300">{title}</h3>
                <p className="mt-4 text-white">{message}</p>
                <button
                    onClick={onClose}
                    className={PRIMARY_BTN_CLASSES.replace('p-2 px-4', 'p-2 mt-6')}
                >
                    OK
                </button>
            </div>
        </div>
    );

    // Critique Modal Component
    const CritiqueModal = ({ critique, title, onClose }) => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
            <div className="w-full max-w-lg p-6 border border-yellow-500 rounded-lg shadow-xl bg-slate-700">
                <h3 className="mb-3 text-2xl font-bold text-yellow-300">Critique: {title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white whitespace-pre-wrap">{critique}</p>
                <button
                    onClick={onClose}
                    className={PRIMARY_BTN_CLASSES.replace('p-2 px-4', 'p-2 mt-6').replace('bg-indigo-500', 'bg-yellow-600').replace('hover:bg-indigo-400', 'hover:bg-yellow-700')}
                >
                    Close
                </button>
            </div>
        </div>
    );

    // Context Display Component
    const ContextDisplay = ({ data, title, isLoading, isFullScreen }) => {
        // Only show the container if data is available or loading is active
        if (!data && !isLoading) return null;

        // Dynamic max-width for content sections
        // Max-width increases significantly when in full-screen mode to utilize the extra column space
        const contentMaxWidthClass = isFullScreen ? 'max-w-full' : 'max-w-lg';

        return (
            <div className={`w-full p-4 bg-slate-600 rounded-xl shadow-lg mt-4 ${contentMaxWidthClass}`}>
                <h3 className="flex items-center pb-2 mb-2 text-lg font-bold text-yellow-300 border-b border-slate-600">
                    <LucideBookOpen size={20} className="inline-block mr-2" /> Art History Context for "{title}"
                </h3>
                {isLoading ? (
                    <p className="py-4 italic text-center text-indigo-300">Loading historical context...</p>
                ) : (
                    <>
                        <p className="p-3 mb-3 text-sm whitespace-pre-wrap border rounded-lg shadow-inner bg-slate-700 text-slate-200 border-slate-500">
                            {data.text}
                        </p>
                        {data.sources.length > 0 && (
                            <div className="mt-3">
                                <p className="mb-1 text-xs font-semibold text-slate-400">Sources (Grounded by Google Search):</p>
                                <ul className="space-y-1 text-xs text-indigo-300 list-disc list-inside">
                                    {data.sources.map((source, index) => (
                                        <li key={index}>
                                            <a href={source.uri} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                                {source.title || source.uri}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {data.sources.length === 0 && data.text !== "Failed to find historical context. Please try again." && (
                            <p className="text-xs italic text-slate-400">No external sources were explicitly used for this context.</p>
                        )}
                    </>
                )}
            </div>
        );
    };

    // QR Code display component that uses a public API
    const QRCodeDisplay = ({ value }) => {
        const qrUrl = `https://chart.googleapis.com/chart?chs=150x150&cht=qr&chl=${encodeURIComponent(value)}`;
        return <img src={qrUrl} alt="QR Code" className="w-24 h-24 border-2 border-indigo-400 rounded-lg" />;
    };

    // Determine the width class for the main content area
    const mainContentWidthClass = isFullScreen ? 'w-full' : 'w-full md:w-3/4';

    // Determine the max-width class for content elements inside the main area
    const contentMaxWidthClass = isFullScreen ? 'max-w-full' : 'max-w-lg';

    return (
        <div className="flex w-screen h-screen p-4 overflow-hidden font-sans antialiased sm:p-8 bg-slate-800 text-slate-200">

            {/* --- Left Pane (Sidebar, Controls, Ads) --- */}
            {/* NO CHANGE: The sidebar remains visible regardless of isFullScreen state */}
            <div className={`flex flex-col w-full md:w-1/4 md:min-w-[280px] p-4 bg-slate-700 rounded-lg shadow-xl space-y-4 md:mr-4 overflow-y-auto transition-all duration-300`}>

                {/* Logo and URL Input - MODIFIED */}
                <div className="flex flex-col items-center p-2 pb-4 border-b border-slate-600">
                    <div className="flex items-center justify-center w-full h-24 mb-4 overflow-hidden bg-indigo-700 rounded-lg shadow-lg">
                        <img
                            src={logoUrl}
                            alt="App Logo"
                            className="object-cover w-full h-full rounded-lg"
                            // Fallback image in case the URL is bad
                            onError={(e) => {
                                e.target.onerror = null; // prevents infinite loop
                                e.target.src = 'https://placehold.co/200x100/9F7AEA/FFFFFF?text=Invalid+URL';
                            }}
                        />
                    </div>

                    {/* Logo URL already loaded from state above */}
                </div>

                {/* NOW CONTAINS JOPPER DETAILS */}
                <div className="flex flex-col items-center p-2 rounded-lg shadow-inner bg-slate-600">
                    <h3 className="mb-2 text-lg font-bold text-yellow-300">Welcome to Jopper</h3>
                    <div className="w-full p-3 text-white border rounded-md shadow-lg bg-slate-800 border-green-500/50">
                        <div className="text-sm leading-relaxed text-slate-300"></div>
                        <div className="font-bold text-indigo-300"></div>A contemporary online art gallery powered by cutting-edge AI artworks and more.
                        Explore a curated world of AI-generated artworks, enriched with creative insights and contextual information.
                        Discover, save, and share your favorite pieces through a sleek, intuitive interface that redefines how you engage with the evolving landscape of digital artworks.
                        <br />
                        <br />
                        <strong>📱 Mobile Users (iOS/Android):</strong> For best viewing experience, tap your browser's top-right menu (the 3 dots), scroll down and select "Desktop Site".
                        <br />
                        <br />
                        <strong>🌟 Improvements:</strong> Auto play button. Enhanced image resolution. More enhancements underway.
                        <br />
                        <br />
                        No app download required, no extra setup — just pure streaming convenience. Happy viewing, happy Joppering! 👌
                    </div>
                </div>

                {/* Sponsored Content Ad Panel */}
                <div className="flex flex-col items-center p-2 rounded-lg shadow-inner bg-slate-600">
                    <h3 className="mb-2 text-lg font-semibold text-yellow-300">Sponsored Ad</h3>
                    <div className="w-full p-2 overflow-hidden text-white rounded-md shadow-lg h-70 bg-slate-700">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: `<a href="https://amzn.to/4qZjaa2" target="_blank" rel="noopener noreferrer" style="color: #6ee7b7; text-decoration: none;">
                                <div style="font-family: sans-serif; background-color: #1e293b; padding: 5px; border-radius: 4px; text-align: center; height: 100%; display: flex; flex-direction: column; justify-content: center;">
                                    <h4 style="font-size: 0.9em; color: white; margin: 0;">Take your athletic performance to the next level with Oakley™. A Meta Vanguard with Meta AI, Audio, Photo and Video Compatibility - White, Prizm™ Black</h4>
                                    <br />
                                    <p style="font-size: 0.9em; margin: 0; color: grey-white"><< Visit Meta Store Now! >></p>
                                </div>
                            </a>` }}
                        />
                    </div>
                </div>

                {/* Ad Code Input Section (for Ad Modal 1) */}
                {isEditor && (
                    <div className="flex flex-col items-center p-2 pt-4 space-y-2 border-t border-slate-600">
                        <h3 className="text-sm font-semibold text-yellow-400">💰 Ad HTML Input (Modal)</h3>
                        <textarea
                            value={adHtmlInput}
                            onChange={(e) => setAdHtmlInput(e.target.value)}
                            placeholder="Paste your ad HTML here..."
                            rows="4"
                            className="w-full p-2 text-xs text-white border rounded-lg resize-none bg-slate-800 placeholder-slate-400 border-slate-600 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                        />
                    </div>
                )}

                {/* Carousel Display Area */}
                <div className="w-full max-w-4xl aspect-[3/2] rounded-2xl overflow-hidden shadow-[0_0_60px_-15px_rgba(99,102,241,0.5)]">
                    <img
                        key={imageIndex} // Key change forces re-render/transition
                        src={currentImageUrl}
                        alt={`Artwork ${imageIndex + 1}`}
                        className="object-cover w-full h-full transition-opacity duration-700 ease-in-out"
                        style={{ animation: 'fadeIn 0.7s forwards' }}
                    />

                    {/* Status Overlay */}
                    <div className="absolute top-0 right-0 px-3 py-1 m-4 text-sm font-medium text-white rounded-full bg-black/60">
                        {isAutoPlaying ? `Auto-Playing (${CYCLE_DURATION_MS / 1000}s)` : 'Manual Mode'}
                    </div>
                </div>

                {/* Feature Buttons */}
                <div className="flex flex-col items-center p-2 pt-4 space-y-2 border-t border-slate-600">
                    <button onClick={toggleFullscreen} className={SECONDARY_BTN_CLASSES}>
                        <LucideMaximize size={14} className="inline-block mr-2" /> {isFullScreen ? 'Exit Image Expand View' : 'Expand Image View'}
                    </button>
                    <button onClick={toggleFavoritesView} className={SECONDARY_BTN_CLASSES}>
                        <LucideStar size={14} className="inline-block mr-2" /> {showFavorites ? 'Back to Gallery' : 'View Favorites'} ({favoriteImages.length})
                    </button>
                    <button onClick={() => handleDataAction('favorites', null, 'toggle_favorite')} className={SECONDARY_BTN_CLASSES}>
                        <LucideHeart size={14} className="inline-block mr-2" /> {currentArt.favorites?.includes(userId) ? 'Unfavorite' : 'Add to Favorites'}
                    </button>
                    <button onClick={() => setShowQR(prev => !prev)} className={SECONDARY_BTN_CLASSES}>
                        <LucideShare2 size={14} className="inline-block mr-2" /> Share via QR
                    </button>
                    {showQR && (
                        <div className="p-3 mt-2 bg-white rounded-lg shadow-lg">
                            <QRCodeDisplay value={currentImageUrl} />
                        </div>
                    )}
                </div>

                {/* Sponsored Content Ad Panel */}
                <div className="flex flex-col items-center p-2 rounded-lg shadow-inner bg-slate-600">
                    <h3 className="mb-2 text-lg font-semibold text-yellow-300">Sponsored Ad</h3>
                    <div className="w-full p-2 overflow-hidden text-white rounded-md shadow-lg h-70 bg-slate-700">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: `<a href="https://amzn.to/4i3n51v" target="_blank" rel="noopener noreferrer" style="color: #6ee7b7; text-decoration: none;">
                                <div style="font-family: sans-serif; background-color: #1e293b; padding: 5px; border-radius: 4px; text-align: center; height: 100%; display: flex; flex-direction: column; justify-content: center;">
                                    <h4 style="font-size: 0.9em; color: white; margin: 0;">Harry Porter and the Sorcerer's Stone (The Full Cast Edition).
                                    Immerse yourself in the stories like never before @ $0.99 per month for 3 months.</h4>
                                    <br />
                                    <p style="font-size: 0.9em; margin: 0; color: grey-white"><< Visit Audible Audiobook Store >></p>
                                </div>
                            </a>` }}
                        />
                    </div>
                </div>

                {/* Social Media Buttons */}
                <div className="flex justify-center p-2 pt-4 space-x-3 border-t border-slate-600">
                    <button onClick={() => shareOnTwitter(currentImageUrl)} className="flex items-center justify-center w-8 h-8 p-2 transition-colors rounded-full shadow-md bg-slate-600 hover:bg-slate-500 text-slate-200">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M18.244 2.25h3.308l-7.227 8.261 8.502 11.239h-6.797l-5.076-6.686-6.195 6.686h-3.308l7.462-8.59-8.455-11.161h6.847l4.904 6.46 5.86-6.46zM17.41 21.697h2.33L8.74 4.398H6.41z" />
                        </svg>
                    </button>
                    <button onClick={() => shareOnFacebook(currentImageUrl)} className="flex items-center justify-center w-8 h-8 p-2 transition-colors rounded-full shadow-md bg-slate-600 hover:bg-slate-500 text-slate-200">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.873V14.894h-3.38v-2.88h3.38v-2.128c0-3.327 1.954-5.161 5.093-5.161 1.488 0 2.91.267 2.91.267v3.18H18.06c-1.657 0-2.16.897-2.16 2.196v2.571h3.764l-.604 2.88h-3.16V22C17.382 21.164 22 17.154 22 12z" />
                        </svg>
                    </button>
                </div>

                {/* Ad Code Input Section (for Ad Modal 2) */}
                {isEditor && (
                    <div className="flex flex-col items-center p-2 pt-4 space-y-2 border-t border-slate-600">
                        <h3 className="text-sm font-semibold text-yellow-400">💰 Ad HTML Input (Modal)</h3>
                        <textarea
                            value={adHtmlInput}
                            onChange={(e) => setAdHtmlInput(e.target.value)}
                            placeholder="Paste your ad HTML here..."
                            rows="4"
                            className="w-full p-2 text-xs text-white border rounded-lg resize-none bg-slate-800 placeholder-slate-400 border-slate-600 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                        />
                        <button onClick={saveAdCode} className={PRIMARY_BTN_CLASSES.replace('bg-indigo-500', 'bg-yellow-600').replace('hover:bg-indigo-400', 'hover:bg-yellow-700').replace('text-white', 'text-slate-900').replace('p-2 px-4', 'w-full p-2 text-xs')}>
                            <LucideSave size={14} className="inline-block mr-1" /> Save Ad Code
                        </button>
                    </div>
                )}

                {/* User Info */}
                <div className="pt-4 mt-auto border-t border-slate-500">
                    <p className="text-xs text-center text-slate-500">
                        User ID: {userId ? userId : 'Authenticating...'}
                    </p>
                    <p className="text-center text-[0.8rem] text-slate-500 mt-1">
                        App ID: {appId}
                    </p>

                    {/* ===== Footer ===== */}
                    <p className="py-2 mx-auto mt-auto text-xs text-center text-gray-300">
                        &copy; 2026 Jopper AI Art Gallery. All rights reserved.
                    </p>
                </div>
            </div>

            {/* --- Main Content (Image, Leaderboard, Comments) --- */}
            {/* UPDATED: Dynamically set width to 'w-full' when expanded */}
            <div className={`flex-1 flex flex-col items-center p-4 bg-slate-700 rounded-lg shadow-xl overflow-y-auto transition-all duration-300 ${mainContentWidthClass}`}>

                {/* Leaderboard Ad Space */}
                <div className={`w-full mb-4 ${contentMaxWidthClass}`}>
                    <div dangerouslySetInnerHTML={{ __html: savedAdHtml }} />
                </div>

                {/* --- Main Application Title Block (UPDATED) --- */}
                <div className="w-full max-w-4xl mb-4 text-center">
                    {/* Applying gradient to the title halves and solid yellow to the pipe separator */}
                    <h2 className="text-lg font-bold tracking-tight text-white">
                        <span className="bg-clip-text text-slate-200 bg-linear-to-r from-indigo-300 to-yellow-300">
                            AI Art Viewer
                        </span>
                        <span className="mx-2 text-yellow-400">|</span>
                        <span className="bg-clip-text text-slate-200 bg-linear-to-r from-indigo-300 to-yellow-300">
                            Online Art Gallery
                        </span>
                    </h2>
                    <p className="mt-1 text-sm text-slate-200">Explore AI-Generated Artwork and Community Insights</p>
                </div>
                {/* --- End Main Application Title Block --- */}

                {/* Image and Navigation */}
                {/* UPDATED: Use contentMaxWidthClass to make the image container expand */}
                <div className={`flex flex-col items-center w-full mb-4 sm:mb-6 p-4 bg-slate-600 rounded-xl shadow-2xl ${contentMaxWidthClass}`}>

                    {/* Image itself expands responsively within the container */}
                    <img
                        src={currentImageUrl}
                        alt="Current Art"
                        // Enforcing a responsive 3:4 aspect ratio for consistent rendering
                        className="object-contain w-full h-full mb-4 border-4 rounded-lg shadow-lg aspect-none border-slate-500"
                    />

                    {/* Previous Button */}
                    <div className="flex w-full gap-3 px-2">
                        <button
                            onClick={() => navigateImage(-1)}
                            className={SECONDARY_BTN_CLASSES.replace('w-full', 'w-1/3')}
                        >
                            <LucideChevronLeft size={14} className="inline-block mr-1" /> Previous
                        </button>

                        {/* Auto Swipe/Stop Button */}
                        <button
                            onClick={toggleAutoPlay}
                            className={SECONDARY_BTN_CLASSES.replace('w-full', 'w-1/3')}
                        >
                            {isAutoPlaying ? (
                                <LucideSquare size={14} className="mr-1" />
                            ) : (
                                <LucidePlay size={14} className="mr-1" />
                            )}
                            {isAutoPlaying ? 'Stop Auto' : 'Auto Play'}
                        </button>

                        {/* Image Index Counter */}
                        <span className="flex items-center px-2 text-xs text-slate-400">
                            {showFavorites
                                ? favoriteImages.length > 0 ? `Favorite ${favoriteImages.indexOf(encodedUrlKey) + 1} of ${favoriteImages.length}` : 'No Favorites'
                                : `${imageIndex + 1} of ${IMAGE_URLS.length}`}
                        </span>

                        {/* Next Button */}
                        <button onClick={() => navigateImage(1)} className={SECONDARY_BTN_CLASSES.replace('w-full', 'w-1/3')}>
                            Next <LucideChevronRight size={14} className="inline-block ml-1" />
                        </button>
                    </div>
                </div>

                {/* Actions and Stats */}
                <div className="flex flex-wrap justify-center w-full max-w-lg gap-3 p-3 mb-4 shadow-inner sm:mb-6 bg-slate-600 rounded-xl">
                    <button onClick={() => handleDataAction('likes', null, 'like')} className={PRIMARY_BTN_CLASSES}>
                        <LucideHeart size={14} className="inline-block mr-1" /> Like ({currentArt.likes || 0})
                    </button>

                    <button onClick={generateCritique} disabled={isCritiquing} className={`${PRIMARY_BTN_CLASSES} disabled:opacity-50`}>
                        <LucideSparkles size={14} className="inline-block mr-1" /> {isCritiquing ? 'Analyzing...' : '✨ AI Critique'}
                    </button>

                    <button onClick={generateHistoricalContext} disabled={isContextLoading} className={`${PRIMARY_BTN_CLASSES.replace('bg-indigo-500', 'bg-emerald-500').replace('hover:bg-indigo-400', 'hover:bg-emerald-400')} disabled:opacity-50`}>
                        <LucideBookOpen size={14} className="inline-block mr-1" /> {isContextLoading ? 'Context Loading...' : '✨ Art History'}
                    </button>
                </div>

                {/* --- Static Text/Hyperlink Ad --- */}
                <div className="w-full max-w-lg mb-4 text-center p-3 bg-slate-800 rounded-lg shadow-inner border border-yellow-500 font-family: sans-serif;">
                    <a
                        href="https://amzn.to/3LBa96N"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-gray-200 underline transition-colors hover:text-indigo-300"
                    >
                        Capture action-packed moments, get workout metrics, listen to your hype mix and more.
                        Oakley™, a Meta Vanguard with Meta AI, Audio, Photo and Video Compatibility - Black, Prizm™ 24 Karat Gold.
                        Limited edition!
                    </a>
                </div>

                {/* Comments Section */}
                <div className="w-full max-w-lg p-4 shadow-lg bg-slate-600 rounded-xl">
                    <h3 className="pb-2 mb-2 font-semibold text-indigo-200 text-xm">
                        <LucideMessageCircle size={20} className="inline-block mr-2" /> Comments: ({currentArt.comments?.length || 0})
                    </h3>
                    <div className="h-32 p-3 mb-4 overflow-y-auto text-sm border rounded-lg shadow-inner bg-slate-700 text-slate-200 border-slate-500">
                        {(currentArt.comments || []).length > 0 ? (
                            // Using array index as key is acceptable here since the comment order is immutable (time-based appending)
                            (currentArt.comments || []).map((c, i) => <p key={i} className="py-1 mb-1 border-b border-slate-600 last:border-b-0">{c}</p>)
                        ) : (
                            <p className="italic text-slate-400">No comments yet. Be the first!</p>
                        )}
                    </div>
                    <div className="flex space-x-2">
                        <input
                            type="text"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            onKeyDown={(e) => {
                                // Allow submission via Enter key
                                if (e.key === 'Enter') {
                                    handleDataAction('comments', comment, 'add_comment');
                                }
                            }}
                            placeholder="Post a comment..."
                            className="flex-1 p-2 text-sm italic text-white border rounded-lg bg-slate-700 placeholder-slate-400 border-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <button onClick={() => handleDataAction('comments', comment, 'add_comment')} className={PRIMARY_BTN_CLASSES.replace('p-2 px-4', 'p-2 px-4')}>
                            Post
                        </button>
                    </div>
                </div>

                {/* New Context Display */}
                <ContextDisplay data={contextData} title={currentArtTitle} isLoading={isContextLoading} isFullScreen={isFullScreen} />
            </div>

            {/* --- Modals --- */}
            {message && message.component
                ? <message.component />
                : message && <MessageModal title={message.title} message={message.msg} onClose={() => setMessage(null)} />
            }
            {critique && <CritiqueModal critique={critique} title={currentArtTitle} onClose={() => setCritique(null)} />}
        </div>
    );
};

export default App;
